/** Container managing 5 or 6 Reel instances */

import { Container, Graphics } from 'pixi.js';
import { Reel, ReelConfig } from './Reel';
import { GameConfig } from '../config/GameConfig';
import { BASE_GAME_STRIPS, FREE_GAME_STRIPS } from '../config/ReelStrips';
import { AudioManager } from '../audio/AudioManager';
import { Logger } from '../utils/Logger';

const logger = new Logger('ReelSet');

export type ReelSetEventType = 'allReelsStopped';
export type ReelSetEventCallback = () => void;

/** Manages the full set of reels and coordinates spin/stop sequences */
export class ReelSet extends Container {
  private _reels: Reel[] = [];
  private _hiddenReels: Reel[] = []; // Extra reels for math only (not displayed)
  private _isBaseGame: boolean = true;
  private _reelSetListeners: Map<ReelSetEventType, Set<ReelSetEventCallback>> = new Map();
  private _gridMask: Graphics | null = null;
  private _separators: Graphics | null = null;

  constructor() {
    super();
    this._createReels(true);
    logger.info('ReelSet initialized');
  }

  /** Visual reel count (what's displayed on screen) */
  get reelCount(): number {
    return this._isBaseGame
      ? GameConfig.VISUAL_REEL_COUNT
      : GameConfig.FREE_GAME_REEL_COUNT;
  }

  /** Total reel count including hidden math-only reels */
  get totalReelCount(): number {
    return this._isBaseGame
      ? GameConfig.BASE_REEL_COUNT
      : GameConfig.FREE_GAME_REEL_COUNT;
  }

  /** All reels including hidden ones (for spin result generation) */
  get allReels(): readonly Reel[] {
    return [...this._reels, ...this._hiddenReels];
  }

  get reels(): readonly Reel[] {
    return this._reels;
  }

  /** Subscribe to reel set events */
  onReelEvent(event: ReelSetEventType, callback: ReelSetEventCallback): void {
    if (!this._reelSetListeners.has(event)) {
      this._reelSetListeners.set(event, new Set());
    }
    this._reelSetListeners.get(event)!.add(callback);
  }

  /** Unsubscribe from reel set events */
  offReelEvent(event: ReelSetEventType, callback: ReelSetEventCallback): void {
    this._reelSetListeners.get(event)?.delete(callback);
  }

  private _emitReelEvent(event: ReelSetEventType): void {
    this._reelSetListeners.get(event)?.forEach((cb) => cb());
  }

  /** Switch between base game (6 reels) and free game (5 reels) mode */
  setMode(isBaseGame: boolean): void {
    if (this._isBaseGame === isBaseGame) return;
    this._isBaseGame = isBaseGame;
    this._createReels(isBaseGame);
    logger.info(`ReelSet mode: ${isBaseGame ? 'base' : 'free'} game`);
  }

  /** Start all reels spinning simultaneously (including hidden reels for math) */
  startSpin(): void {
    const audio = AudioManager.getInstance();
    audio.playSpinStart();

    for (const reel of this._reels) {
      reel.startSpin();
    }
    for (const reel of this._hiddenReels) {
      reel.startSpin();
    }
    logger.debug('All reels started spinning');
  }

  /** Stop reels sequentially with configurable delay and scatter anticipation */
  async stopReels(stopPositions: number[], turbo: boolean = false): Promise<void> {
    const audio = AudioManager.getInstance();
    let scatterCount = 0;
    let anticipationActive = false;

    // Stop hidden reels instantly (math only, no animation)
    const visualCount = this._reels.length;
    for (let i = 0; i < this._hiddenReels.length; i++) {
      const stopIdx = visualCount + i;
      if (stopIdx < stopPositions.length) {
        this._hiddenReels[i].snapTo(stopPositions[stopIdx]);
      }
    }

    const reelCount = Math.min(stopPositions.length, visualCount);

    for (let i = 0; i < reelCount; i++) {
      // Wait for reels to get up to speed before issuing first stop
      if (i === 0) {
        const minDuration = turbo
          ? GameConfig.TURBO_SPIN_MIN_DURATION
          : GameConfig.SPIN_MIN_DURATION;
        await this._delay(minDuration);
      }

      await this._reels[i].stopAt(stopPositions[i]);
      audio.playReelStop(i);

      // Count scatter symbols on the reel that just stopped
      const visibleSymbols = this._reels[i].getVisibleSymbols();
      const reelScatters = visibleSymbols.filter((s) => s === 'SCATTER').length;
      if (reelScatters > 0) {
        scatterCount += reelScatters;
        audio.playScatterHit(scatterCount);
        logger.info(`Scatter landed on reel ${i} — total scatters: ${scatterCount}`);
      }

      // Anticipation: only trigger when 2 scatters landed AND at least 2 reels remain
      // (meaning a free game trigger is still possible). Also skip in turbo mode.
      const hasRemainingReels = i < reelCount - 1;
      const remainingReels = reelCount - 1 - i;
      if (scatterCount >= 2 && remainingReels >= 2 && !anticipationActive && !turbo) {
        anticipationActive = true;
        audio.playAnticipation();
        logger.info(`Anticipation triggered after reel ${i} (${scatterCount} scatters, ${remainingReels} reels left)`);

        // Slow down remaining spinning reels
        for (let j = i + 1; j < reelCount; j++) {
          this._reels[j].setAnticipation(true);
        }
      }

      // Delay between reel stops
      if (hasRemainingReels) {
        // Anticipation adds a moderate delay (800ms), not excessive
        const extraDelay = anticipationActive ? 800 : 0;
        const baseDelay = turbo
          ? GameConfig.TURBO_REEL_STOP_DELAY
          : GameConfig.REEL_STOP_DELAY;
        await this._delay(baseDelay + extraDelay);
      }
    }

    // Clean up anticipation state
    if (anticipationActive) {
      audio.stopAnticipation();
      for (const reel of this._reels) {
        reel.setAnticipation(false);
      }
      logger.info('Anticipation ended');
    }

    logger.debug('All reels stopped');
    this._emitReelEvent('allReelsStopped');
  }

  /** Get the currently displayed grid of symbols (visual reels x rows) */
  getGrid(): string[][] {
    return this._reels.map((reel) => reel.getVisibleSymbols());
  }

  /** Get the full grid including hidden reels (for win evaluation) */
  getFullGrid(): string[][] {
    return [...this._reels, ...this._hiddenReels].map((reel) => reel.getVisibleSymbols());
  }

  /** Show or hide the separator lines between reels */
  setSeparatorsVisible(visible: boolean): void {
    if (this._separators) {
      this._separators.visible = visible;
    }
  }

  /** Per-frame update — call from the main game ticker */
  update(delta: number): void {
    for (const reel of this._reels) {
      reel.update(delta);
    }
    for (const reel of this._hiddenReels) {
      reel.update(delta);
    }
  }

  /** Get total width of the visual reel set (excludes hidden math-only reels) */
  getTotalWidth(): number {
    const count = this._reels.length; // Only visual reels
    return count * GameConfig.SYMBOL_WIDTH + (count - 1) * GameConfig.REEL_GAP;
  }

  /** Get total height of the reel set */
  getTotalHeight(): number {
    return GameConfig.VISIBLE_ROWS * GameConfig.SYMBOL_HEIGHT;
  }

  private _createReels(isBaseGame: boolean): void {
    // Remove existing reels
    for (const reel of this._reels) {
      this.removeChild(reel);
      reel.destroy({ children: true });
    }
    for (const reel of this._hiddenReels) {
      reel.destroy({ children: true });
    }
    this._reels = [];
    this._hiddenReels = [];

    const strips = isBaseGame ? BASE_GAME_STRIPS : FREE_GAME_STRIPS;
    const totalCount = isBaseGame ? GameConfig.BASE_REEL_COUNT : GameConfig.FREE_GAME_REEL_COUNT;
    const visualCount = isBaseGame ? GameConfig.VISUAL_REEL_COUNT : GameConfig.FREE_GAME_REEL_COUNT;

    for (let i = 0; i < totalCount; i++) {
      const config: ReelConfig = {
        reelIndex: i,
        symbolWidth: GameConfig.SYMBOL_WIDTH,
        symbolHeight: GameConfig.SYMBOL_HEIGHT,
        visibleRows: GameConfig.VISIBLE_ROWS,
        strip: strips[i],
      };

      const reel = new Reel(config);

      if (i < visualCount) {
        // Visual reel — add to display
        reel.x = i * (GameConfig.SYMBOL_WIDTH + GameConfig.REEL_GAP);
        this.addChild(reel);
        this._reels.push(reel);
      } else {
        // Hidden reel — used for math evaluation only, not added to display
        this._hiddenReels.push(reel);
      }
    }

    logger.debug(`Created ${visualCount} visual reels + ${this._hiddenReels.length} hidden reels`);

    // Recreate mask and separators for visual reel count only
    this._createMask();
    this._createSeparators();
  }

  /** Create a mask that clips symbols to the visible reel area */
  private _createMask(): void {
    if (this._gridMask) {
      this.removeChild(this._gridMask);
      this._gridMask.destroy();
    }
    const w = this.getTotalWidth();
    const h = this.getTotalHeight();
    const halfSym = GameConfig.SYMBOL_WIDTH / 2;
    const halfSymH = GameConfig.SYMBOL_HEIGHT / 2;
    this._gridMask = new Graphics();
    this._gridMask.rect(-halfSym, -halfSymH, w + GameConfig.SYMBOL_WIDTH, h + GameConfig.SYMBOL_HEIGHT).fill(0xffffff);
    this.addChild(this._gridMask);
    this.mask = this._gridMask;
  }

  /** Draw thin vertical separator lines between reels */
  private _createSeparators(): void {
    if (this._separators) {
      this.removeChild(this._separators);
      this._separators.destroy();
    }
    const h = this.getTotalHeight();
    this._separators = new Graphics();
    for (let i = 1; i < this._reels.length; i++) {
      const x = i * (GameConfig.SYMBOL_WIDTH + GameConfig.REEL_GAP) - GameConfig.REEL_GAP / 2;
      this._separators.rect(x - 0.5, 0, 1, h).fill({ color: 0xffffff, alpha: 0.15 });
    }
    this.addChild(this._separators);
  }

  private _delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
